import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from '@angular/core';
import { ExportChangedField } from '@isophi/mng-api';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

export interface SaveAllDialogResult {
  correctionFields: { [fieldName: string]: string };
}

@Component({
  selector: 'app-save-all-dialog',
  templateUrl: './save-all-dialog.component.html',
  styleUrls: ['./save-all-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SaveAllDialogComponent implements OnInit {
  @Input() changedFields: ExportChangedField[] = [];

  /** `null` = the user has not decided yet; such a field blocks the save. */
  fieldChoices: ('update' | 'correction' | null)[] = [];
  /** Set once the user tries to save with undecided fields, so the missing choices get highlighted. */
  submitAttempted = false;
  todayLabel = '';
  fieldLastRecordLabels: string[] = [];
  allCorrection = false;

  constructor(public activeModal: NgbActiveModal, private cdr: ChangeDetectorRef, private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    // Nothing is preselected where the user has a real choice — update vs. correction is reported to MŠMT,
    // so it must be a deliberate decision. Fields that can only be an update need no decision.
    this.fieldChoices = this.changedFields.map((_, i) => (this.canCorrect(i) ? null : 'update'));
    const t = new Date();
    this.todayLabel = `${t.getDate()}. ${t.getMonth() + 1}. ${t.getFullYear()}`;
    this.fieldLastRecordLabels = this.changedFields.map((f) => {
      if (!f.latestHistoryDate) return '';
      const d = new Date(f.latestHistoryDate);
      return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
    });
  }

  /**
   * A correction may be offered for a field only when the backend allows it — i.e. an existing value
   * was overwritten and the field's current history record is not locked. Locked-current fields are
   * always treated as an update (the reported record must stay intact). Without the date of the record
   * to overwrite the correction could not be sent, so it is not offered at all rather than silently
   * falling back to an update.
   */
  canCorrect(index: number): boolean {
    const field = this.changedFields[index];
    return !!field?.correctionAllowed && !!field.latestHistoryDate;
  }

  /**
   * Why a correction is not offered. Derived from the history record, not from the displayed old value
   * (which may be empty even when a value existed): no record means there is nothing to correct; with
   * a record and a known old value the record is locked (reported); otherwise the reason is unknown.
   */
  correctionUnavailableKey(index: number): string {
    const field = this.changedFields[index];
    if (!field?.latestHistoryDate) return 'saveAllDialog/correctionNoRecord';
    if (field.oldValueDisplay) return 'saveAllDialog/correctionLocked';
    return 'saveAllDialog/correctionNotPossible';
  }

  /** Description of the update choice — older values are mentioned only when the field has any history. */
  updateDescKey(index: number): string {
    if (this.updatesTodayRecord(index)) return 'saveAllDialog/updateChoiceDescToday';
    if (!this.changedFields[index]?.latestHistoryDate) return 'saveAllDialog/updateChoiceDescFirst';
    return 'saveAllDialog/updateChoiceDesc';
  }

  /**
   * Whether an update of this field will land on today's existing record rather than create a new one.
   * History keeps one record per field per day, so when the field already changed today an "update"
   * overwrites that record instead of adding a row — the wording must not promise a new record.
   */
  updatesTodayRecord(index: number): boolean {
    const label = this.fieldLastRecordLabels[index];
    return !!label && label === this.todayLabel;
  }

  /** At least one field can be marked as a correction. */
  get canCorrectAny(): boolean {
    return this.changedFields.some((_, i) => this.canCorrect(i));
  }

  /** Number of fields still waiting for the user's update/correction decision. */
  get undecidedCount(): number {
    return this.fieldChoices.filter((c) => c === null).length;
  }

  isUndecided(index: number): boolean {
    return this.fieldChoices[index] === null;
  }

  setChoice(index: number, choice: 'update' | 'correction'): void {
    if (choice === 'correction' && !this.canCorrect(index)) {
      return;
    }
    this.fieldChoices[index] = choice;
    this.syncAllCorrectionFlag();
    this.cdr.markForCheck();
  }

  /** Global "everything is a correction" toggle — only applies to fields where a correction is allowed. */
  toggleAllCorrection(checked: boolean): void {
    this.allCorrection = checked;
    this.fieldChoices = this.changedFields.map((_, i) => {
      if (!this.canCorrect(i)) return 'update';
      return checked ? 'correction' : null;
    });
    this.cdr.markForCheck();
  }

  private syncAllCorrectionFlag(): void {
    this.allCorrection =
      this.canCorrectAny && this.changedFields.every((_, i) => !this.canCorrect(i) || this.fieldChoices[i] === 'correction');
  }

  submit(): void {
    if (this.undecidedCount > 0) {
      this.submitAttempted = true;
      this.cdr.markForCheck();
      // Wait for the highlight to render, then bring the first undecided field into view.
      setTimeout(() =>
        this.elementRef.nativeElement.querySelector('.field-card.undecided')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      );
      return;
    }
    const correctionFields: { [fieldName: string]: string } = {};
    this.changedFields.forEach((field, i) => {
      if (this.fieldChoices[i] === 'correction' && this.canCorrect(i) && field.latestHistoryDate) {
        correctionFields[field.fieldName] = field.latestHistoryDate;
      }
    });
    this.activeModal.close({ correctionFields } as SaveAllDialogResult);
  }
}
